import React, { useEffect, useState } from 'react';
import { CalendarDays, Plus, X, MapPin, Users, CheckCircle2, XCircle, Pencil, Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { MEETING } from '@/constants/testIds';

/* Local helper: ISO → `YYYY-MM-DDTHH:MM` for native datetime-local input. */
function toLocalInput(iso) {
  const d = new Date(iso);
  const pad = (n) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function Meetings() {
  const { user } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // { meeting_id, proposed_at_local, duration_minutes }
  const [form, setForm] = useState({
    title:'', description:'', invitee_user_ids:[], proposed_at:'', duration_minutes:30, room_name:'',
  });

  const startEdit = (m) => setEditing({
    meeting_id: m.meeting_id,
    proposed_at_local: toLocalInput(m.proposed_at),
    duration_minutes: m.duration_minutes,
  });
  const saveEdit = async () => {
    if (!editing) return;
    try {
      await api.patch(`/meetings/${editing.meeting_id}`, {
        proposed_at: new Date(editing.proposed_at_local).toISOString(),
        duration_minutes: editing.duration_minutes,
      });
      toast.success('Rescheduled.');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reschedule.');
    }
  };

  const load = async () => {
    const [m, r] = await Promise.all([api.get('/meetings/'), api.get('/meetings/rooms')]);
    setMeetings(m.data);
    setRooms(r.data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (open) api.get('/users/directory').then(r => setUsers(r.data.filter(u => u.user_id !== user?.user_id)));
  }, [open, user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.proposed_at || form.invitee_user_ids.length === 0) {
      toast.error('Fill title, time and at least one invitee.');
      return;
    }
    try {
      await api.post('/meetings/', {
        ...form,
        proposed_at: new Date(form.proposed_at).toISOString(),
        duration_minutes: parseInt(form.duration_minutes),
      });
      toast.success('Meeting requested.');
      setOpen(false);
      setForm({ title:'', description:'', invitee_user_ids:[], proposed_at:'', duration_minutes:30, room_name:'' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create'); }
  };

  const act = async (id, action) => {
    try {
      await api.post('/meetings/action', { meeting_id: id, action });
      toast.success(`Meeting ${action}d`);
      load();
    } catch { toast.error('Action failed'); }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="ve-eyebrow">Meetings & Rooms</div>
          <h1 className="display text-3xl md:text-4xl mt-2">Coordinate boardrooms in seconds.</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">Request meetings, auto-book rooms, and approve invites — all in one place.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid={MEETING.createBtn} className="ve-btn-primary self-start"><Plus size={14}/> New meeting</button>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2">
          <h2 className="display text-xl mb-4">Upcoming & pending</h2>
          <div className="space-y-3">
            {meetings.length === 0 && <div className="ve-card p-8 text-center text-sm text-slate-500">No meetings yet.</div>}
            {meetings.map(m => {
              const isEditing = editing?.meeting_id === m.meeting_id;
              const canEdit = (m.requester_user_id === user?.user_id) || ['holding_admin', 'system_admin'].includes(user?.role);
              return (
                <div key={m.meeting_id} className="ve-card p-5" data-testid={`meeting-row-${m.meeting_id}`}>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1">
                      {isEditing ? (
                        <div className="flex flex-wrap items-center gap-2" data-testid={`meeting-inline-edit-${m.meeting_id}`}>
                          <input
                            type="datetime-local"
                            value={editing.proposed_at_local}
                            onChange={(e) => setEditing({ ...editing, proposed_at_local: e.target.value })}
                            data-testid={`meeting-edit-time-${m.meeting_id}`}
                            className="text-xs rounded-md border border-slate-300 dark:border-white/15 px-2 py-1.5 bg-transparent focus:outline-none focus:border-[#8B3FC5]"
                          />
                          <input
                            type="number"
                            min="5" max="480" step="5"
                            value={editing.duration_minutes}
                            onChange={(e) => setEditing({ ...editing, duration_minutes: Number(e.target.value) })}
                            data-testid={`meeting-edit-duration-${m.meeting_id}`}
                            className="w-20 text-xs rounded-md border border-slate-300 dark:border-white/15 px-2 py-1.5 bg-transparent focus:outline-none focus:border-[#8B3FC5]"
                          />
                          <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">min</span>
                          <button
                            onClick={saveEdit}
                            data-testid={`meeting-edit-save-${m.meeting_id}`}
                            className="inline-flex items-center gap-1 px-3 h-8 text-[10px] uppercase tracking-[0.18em] font-bold rounded-md bg-[#0A0A0A] text-[#8B3FC5] hover:bg-[#1a1a1a]"
                          ><Save size={11}/> Save</button>
                          <button
                            onClick={() => setEditing(null)}
                            data-testid={`meeting-edit-cancel-${m.meeting_id}`}
                            className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 hover:text-rose-500 px-2"
                          >Cancel</button>
                        </div>
                      ) : (
                        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 flex items-center gap-2">
                          {new Date(m.proposed_at).toLocaleString()} · {m.duration_minutes} min
                          {canEdit && (
                            <button
                              onClick={() => startEdit(m)}
                              data-testid={`meeting-edit-${m.meeting_id}`}
                              title="Reschedule"
                              className="text-slate-400 hover:text-[#5E1F8B] transition"
                            ><Pencil size={11}/></button>
                          )}
                        </div>
                      )}
                      <h3 className="display text-lg mt-1">{m.title}</h3>
                      <p className="text-sm text-slate-600 mt-1">{m.description}</p>
                      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><Users size={12}/>{m.invitee_user_ids?.length || 0} invitees</span>
                        {m.room_name && <span className="flex items-center gap-1"><MapPin size={12}/>{m.room_name}</span>}
                        <span className={`px-2 py-0.5 rounded uppercase tracking-[0.15em] ${
                          m.status === 'approved' ? 'bg-emerald-50 text-emerald-700' :
                          m.status === 'rejected' ? 'bg-rose-50 text-rose-700' :
                          'bg-amber-50 text-amber-700'}`}>{m.status}</span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      {m.status === 'pending' && (
                        <>
                          <button onClick={() => act(m.meeting_id, 'approve')} data-testid={MEETING.approveBtn} className="ve-btn-primary text-xs px-3 py-1.5"><CheckCircle2 size={12}/>Approve</button>
                          <button onClick={() => act(m.meeting_id, 'reject')} className="ve-btn-secondary text-xs px-3 py-1.5"><XCircle size={12}/>Reject</button>
                        </>
                      )}
                      {m.join_url && <a href={m.join_url} target="_blank" rel="noreferrer" className="ve-btn-ghost text-xs">Join →</a>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <aside>
          <h2 className="display text-xl mb-4">Rooms</h2>
          <div className="space-y-3">
            {rooms.map(r => (
              <div key={r.room_id} className="ve-card p-4">
                <div className="font-medium">{r.name}</div>
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin size={11}/>{r.location}</div>
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Users size={11}/>Capacity {r.capacity}</div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {r.amenities?.map(a => <span key={a} className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-600 rounded">{a}</span>)}
                </div>
              </div>
            ))}
          </div>
        </aside>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
          <form onSubmit={submit} className="ve-card p-7 w-full max-w-2xl bg-white">
            <div className="flex items-center justify-between">
              <h2 className="display text-2xl">New meeting</h2>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={18}/></button>
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="md:col-span-2 text-xs uppercase tracking-[0.18em] text-slate-500">Title
                <input required value={form.title} onChange={e => setForm({...form, title:e.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal"/>
              </label>
              <label className="md:col-span-2 text-xs uppercase tracking-[0.18em] text-slate-500">Description
                <textarea rows={3} value={form.description} onChange={e => setForm({...form, description:e.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal"/>
              </label>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-500">When
                <input required type="datetime-local" value={form.proposed_at} onChange={e => setForm({...form, proposed_at:e.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20"/>
              </label>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-500">Duration (min)
                <input type="number" min={15} step={15} value={form.duration_minutes} onChange={e => setForm({...form, duration_minutes:e.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20"/>
              </label>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-500">Room (optional)
                <select value={form.room_name} onChange={e => setForm({...form, room_name:e.target.value})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal">
                  <option value="">— virtual / no room —</option>
                  {rooms.map(r => <option key={r.room_id} value={r.name}>{r.name} · {r.location}</option>)}
                </select>
              </label>
              <label className="text-xs uppercase tracking-[0.18em] text-slate-500">Invitees
                <select multiple value={form.invitee_user_ids} onChange={e => setForm({...form, invitee_user_ids: Array.from(e.target.selectedOptions, o => o.value)})} className="mt-1.5 w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm h-32 focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20 normal-case tracking-normal">
                  {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name} · {u.role}</option>)}
                </select>
              </label>
            </div>
            <div className="mt-7 flex justify-end gap-3">
              <button type="button" onClick={() => setOpen(false)} className="ve-btn-secondary">Cancel</button>
              <button type="submit" className="ve-btn-primary">Send request</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
